import { useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Container } from "../components/Container";
import { Card } from "../components/Card";
import { Button } from "../components/Button";
import colors from "@styling/colors";
import { spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import { useParams } from "react-router-dom";
import { useGetResellerQuery, useUpdateResellerPartialMutation } from "../query/resellersQuery";
import { LoadingContainer } from "../components/LoadingContainer";
import { SidebarPanel } from "../components/SidebarPanel";
import { ResellerForm } from "./components/ResellerForm";
import { useIsAdmin } from "../hooks/useIsAdmin";
import { formatDate } from "../utils/dateUtils";
import { SelectInput } from "../components/SelectInput";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { FirestoreConsoleButton } from "../components/FirestoreConsoleButton";
import { GcpLogsConsoleButton } from "../components/GcpLogsConsoleButton";
import { useSalesPeriodState } from "../hooks/useSalesPeriodState";
import { SalesStatsSection } from "./components/SalesStatsSection";
import { ResellerTopListSection } from "./components/ResellerTopListSection";

const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: ${spacing.x3}px;
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: ${spacing.x2}px;
  }
`;

const InfoCard = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x2}px;
`;

const InfoLabel = styled.span`
  font-size: ${fontSizes.small}px;
  font-weight: 500;
  color: ${colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const InfoValue = styled.span`
  font-size: ${fontSizes.large}px;
  font-weight: 500;
  color: ${colors.text};
`;

const InfoRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x05}px;
`;

const SelectWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.x2}px;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const PageHeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.x2}px;
`;

const LogoImage = styled.img`
  max-width: 200px;
  max-height: 200px;
  margin-top: ${spacing.x1}px;
  border-radius: 4px;
`;

export const ResellerOverview = () => {
  const { t } = useTranslation();
  const { resellerId } = useParams<{ resellerId: string }>();
  const { data: reseller, isLoading } = useGetResellerQuery(resellerId);
  const [isEditPanelOpen, setIsEditPanelOpen] = useState(false);
  const isAdmin = useIsAdmin();
  const periodState = useSalesPeriodState();
  const { mutate: updateResellerPartialMutation, isPending } = useUpdateResellerPartialMutation();

  const handleBrandChange = (e: { target: { value: string } }) => {
    const newBrand = e.target.value;
    if (resellerId && reseller) {
      updateResellerPartialMutation({
        resellerId: resellerId,
        reseller: { brand: newBrand },
      });
    }
  };

  const handleEditSuccess = () => {
    setIsEditPanelOpen(false);
  };

  if (isLoading) {
    return <LoadingContainer message={t("resellerOverview.loading")} />;
  }
  if (!reseller) {
    return <LoadingContainer message={t("resellerOverview.notFound")} />;
  }
  return (
    <Container>
      <PageHeader>
        <h1 style={{ margin: 0 }}>{t("resellerOverview.pageTitle")}</h1>
        <PageHeaderActions>
          <FirestoreConsoleButton path={`resellers/${resellerId ?? ""}`} />
          <GcpLogsConsoleButton searchId={resellerId ?? ""} />
          <Button onClick={() => setIsEditPanelOpen(true)}>{t("resellerOverview.editReseller")}</Button>
        </PageHeaderActions>
      </PageHeader>

      <SalesStatsSection resellerId={resellerId} periodState={periodState} />

      <ResellerTopListSection resellerId={resellerId} />

      <DashboardGrid>
        <InfoCard>
          {reseller.logo && (
            <InfoRow>
              <LogoImage src={reseller.logo} alt={t("resellerOverview.logoAlt")} />
            </InfoRow>
          )}
          <InfoRow>
            <InfoLabel>{t("resellerOverview.resellerName")}</InfoLabel>
            <InfoValue>{reseller.name}</InfoValue>
          </InfoRow>
          {isAdmin && reseller.id && (
            <InfoRow>
              <InfoLabel>{t("resellerOverview.id")}</InfoLabel>
              <InfoValue>{reseller.id}</InfoValue>
            </InfoRow>
          )}
          {isAdmin && (
            <InfoRow>
              <InfoLabel>{t("resellerOverview.brand")}</InfoLabel>
              <SelectWrapper>
                <SelectInput
                  value={reseller.brand || "Kassaro"}
                  onChange={handleBrandChange}
                  disabled={isPending}
                >
                  <option value="Kassaro">Kassaro</option>
                </SelectInput>
                {isPending && (
                  <LoadingSpinner size={16} borderWidth={2} />
                )}
              </SelectWrapper>
            </InfoRow>
          )}
        </InfoCard>
        <InfoCard>
          <InfoRow>
            <InfoLabel>{t("resellerOverview.contactEmail")}</InfoLabel>
            <InfoValue>{reseller.contactEmail}</InfoValue>
          </InfoRow>
          {reseller.contactEmailInfo && (
            <InfoRow>
              <InfoLabel>{t("resellerOverview.contactEmailInfo")}</InfoLabel>
              <InfoValue>{reseller.contactEmailInfo}</InfoValue>
            </InfoRow>
          )}
          {reseller.contactPhone && (
            <InfoRow>
              <InfoLabel>{t("resellerOverview.contactPhone")}</InfoLabel>
              <InfoValue>{reseller.contactPhone}</InfoValue>
            </InfoRow>
          )}
          {reseller.contactPhoneInfo && (
            <InfoRow>
              <InfoLabel>{t("resellerOverview.contactPhoneInfo")}</InfoLabel>
              <InfoValue>{reseller.contactPhoneInfo}</InfoValue>
            </InfoRow>
          )}
        </InfoCard>
        <InfoCard>
          <InfoRow>
            <InfoLabel>{t("resellerOverview.organizationNumber")}</InfoLabel>
            <InfoValue>{reseller.orgNumber}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>{t("resellerOverview.address")}</InfoLabel>
            <InfoValue>
              {reseller.street}
              <br />
              {reseller.postalcode} {reseller.city}
            </InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>{t("resellerOverview.country")}</InfoLabel>
            <InfoValue>{reseller.country}</InfoValue>
          </InfoRow>
        </InfoCard>
        {reseller.createdAt && (
          <InfoCard>
            <InfoRow>
              <InfoLabel>{t("resellerOverview.createdAt")}</InfoLabel>
              <InfoValue>{formatDate(reseller.createdAt)}</InfoValue>
            </InfoRow>
          </InfoCard>
        )}
      </DashboardGrid>

      {isEditPanelOpen && (
        <SidebarPanel
          isOpen={isEditPanelOpen}
          onClose={() => setIsEditPanelOpen(false)}
          title={t("resellerOverview.editPanelTitle", { name: reseller.name })}
        >
          <ResellerForm
            reseller={reseller}
            resellerId={resellerId}
            onSuccess={handleEditSuccess}
            onCancel={() => setIsEditPanelOpen(false)}
          />
        </SidebarPanel>
      )}
    </Container>
  );
};
